import type { MeResponse } from "@salon-pos/shared";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { ApiError, api, login as apiLogin, logout as apiLogout, setCsrf, type TwoFactorStep } from "./api";
import { setStaffNouns } from "./i18n/noun";

export interface AuthState {
  me: MeResponse | null;
  loading: boolean;
  /** null = signed in; a step = the account needs its authenticator code first (see verifyTwoFactor + adopt) */
  login: (email: string, password: string) => Promise<TwoFactorStep | null>;
  logout: () => Promise<void>;
  switchTo: (body: { membershipId?: string; branchId?: string }) => Promise<void>;
  refresh: () => Promise<void>;
  /** Use a /me payload returned by another endpoint (signup, setup finish). */
  adopt: (me: MeResponse) => void;
}

/** Exported for tests that render a page with a given signed-in user. */
export const AuthContext = createContext<AuthState | null>(null);
const Ctx = AuthContext;

const IS_NATIVE = import.meta.env.VITE_CLIENT === "native";
/**
 * (App Store 2.1 / 3.1) the store app shows the customer-subscriptions module only when this branch can really sell one
 * (the shop's Stripe is connected and at least one plan is on sale). Otherwise it is hidden everywhere in the app
 * (nav, POS button, route) instead of showing a dead-end "connect Stripe in Settings" dialog. The web is unchanged.
 */
async function storeSubsUsable(m: MeResponse): Promise<boolean> {
  const b = encodeURIComponent(m.activeBranchId ?? "");
  try {
    const [ready, plans] = await Promise.all([
      api<{ ready: boolean }>("/api/subscriptions/readiness?branchId=" + b),
      api<{ plans: Array<{ active?: boolean }> }>("/api/subscription-plans?branchId=" + b),
    ]);
    return ready.ready === true && plans.plans.some((p) => p.active !== false);
  } catch (e) { return !(e instanceof ApiError && (e.status === 403 || e.status === 404)); }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // the salon's industry decides the staff noun used across the UI (服务人员 / 技师 / 美甲师 …)
  const apply = (m: MeResponse | null) => {
    setMe(m); setStaffNouns(m?.tenant?.businessTypes ?? []); if (m) setCsrf(m.csrfToken);
    if (m && IS_NATIVE && m.features.includes("subscriptions")) void storeSubsUsable(m).then((ok) => {
      if (!ok) setMe((cur) => (cur === m ? { ...m, features: m.features.filter((f) => f !== "subscriptions") } : cur));
    });
  };

  const refresh = useCallback(async () => {
    try { apply(await api<MeResponse>("/api/me")); }
    catch (e) { if (e instanceof ApiError && e.status === 401) apply(null); else throw e; }
  }, []);

  useEffect(() => { refresh().catch(() => apply(null)).finally(() => setLoading(false)); }, [refresh]);
  // (2026-10-10) a 403 email_unverified anywhere: reload /me so the "Verify your email" gate appears
  useEffect(() => {
    const on = () => { void refresh().catch(() => undefined); };
    window.addEventListener("spos:email-unverified", on);
    return () => window.removeEventListener("spos:email-unverified", on);
  }, [refresh]);

  const value: AuthState = {
    me, loading, refresh, adopt: apply,
    login: async (email, password) => {
      const r = await apiLogin(email, password);
      if (r.twoFactor) return r.twoFactor;
      apply(r.me);
      return null;
    },
    logout: async () => { await apiLogout(); apply(null); },
    switchTo: async (body) => apply(await api<MeResponse>("/api/session/switch", { method: "POST", body })),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
